import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SplitAuthLayout } from '@/layouts/SplitAuthLayout';
import { LoginMethodTabs } from '@/components/auth/LoginMethodTabs';
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons';
import { useAuth } from '@/contexts/AuthContext';
import { getSafeRedirect } from '@/lib/authRedirect';

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

const AlertIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#C8553D"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="mt-[1px] shrink-0"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5" />
    <path d="M12 16.5h.01" />
  </svg>
);

export default function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  // True from password submit until sign-in (including any TOTP step) is done.
  const [signingIn, setSigningIn] = useState(false);
  const params = new URLSearchParams(location.search);
  const isLocalSignedOut = DISABLE_AUTH && params.get('signed_out') === '1';
  const redirectTo = getSafeRedirect(params.get('redirect'));

  // Redirect once authenticated. Wait while a sign-in is in flight: password
  // sign-in sets `user` before the TOTP code is verified.
  // Skip if we arrived here via account deactivation — signOut() is still
  // in-flight so user may still be non-null, and we must not bounce back.
  useEffect(() => {
    if (DISABLE_AUTH && !isLocalSignedOut) {
      navigate('/', { replace: true });
      return;
    }
    if (user && !signingIn && !location.state?.deactivating) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, signingIn, navigate, location.state, isLocalSignedOut, redirectTo]);

  if (isLocalSignedOut) {
    return (
      <SplitAuthLayout>
        <div className="flex flex-col gap-7">
          <div className="flex flex-col gap-2">
            <h2 className="m-0 font-display text-[32px] font-normal leading-[1.1] tracking-[-0.02em] text-ink">
              Signed out
            </h2>
            <p className="m-0 text-[15px] leading-[1.5] text-ink-2">
              Local auth bypass is enabled for this dev session.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className="inline-flex h-11 items-center justify-center rounded-md bg-coral px-4 text-sm font-medium text-white hover:bg-coral-hover"
          >
            Return to app
          </button>
        </div>
      </SplitAuthLayout>
    );
  }

  if (DISABLE_AUTH) return null;

  const isCredsError = error?.includes('Invalid login credentials');

  return (
    <SplitAuthLayout>
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-2">
          <h2 className="m-0 font-display text-[32px] font-normal leading-[1.1] tracking-[-0.02em] text-ink">
            Welcome back
          </h2>
          <p className="m-0 text-[15px] leading-[1.5] text-ink-2">
            Sign in to continue your content workflow.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-[10px] border border-[#C8553D]/30 bg-[#FBEEEA] px-[14px] py-3"
          >
            <AlertIcon />
            <div className="flex flex-col gap-[3px]">
              <p className="m-0 text-[13.5px] font-medium leading-[1.3] text-[#7A2E1F]">
                {isCredsError ? "We couldn't sign you in." : error}
              </p>
              {isCredsError && (
                <p className="m-0 text-[13px] leading-[1.45] text-[#8A4A3A]">
                  If you signed up with Google, use Continue with Google instead
                  of a password.
                </p>
              )}
            </div>
          </div>
        )}

        <LoginMethodTabs
          onSigningInChange={setSigningIn}
          onError={(msg) => {
            setError(msg);
          }}
          forgotPasswordSlot={
            <div className="flex justify-end -mt-0.5">
              <Link
                to="/password-reset"
                className="text-[13px] text-ink-2 no-underline hover:text-ink border-b border-dotted border-ink-3"
              >
                Forgot your password?
              </Link>
            </div>
          }
        />

        <div className="flex items-center gap-3 text-ink-3">
          <div className="h-px flex-1 bg-warm-border" />
          <span className="font-mono text-[11px] uppercase tracking-[0.12em]">
            or
          </span>
          <div className="h-px flex-1 bg-warm-border" />
        </div>

        <SocialLoginButtons />

        <p className="m-0 text-center text-[13px] text-ink-3">
          Don't have an account?{' '}
          <Link
            to="/signup"
            className="font-medium text-coral-dark no-underline border-b border-coral-dark/30 hover:border-coral-dark"
          >
            Sign up
          </Link>
        </p>
      </div>
    </SplitAuthLayout>
  );
}
